import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const TheHomePage = () => {
  const { language } = useLanguage();

  const images = [
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/00ila1ig_DJI_0138.jpg",
      alt: "Aerial view of The Home"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/2k7pddjr_IMG_20260222_135522.jpg",
      alt: "Pool",
      position: "top"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/7zbp8jp2_0S0A3560.jpg",
      alt: "Inside space - bedroom"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/93m2uqqm_IMG-20250507-WA0017.jpg",
      alt: "Horses at The Home"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/b4am2jgm_IMG-20241130-WA0022.jpg",
      alt: "Sheep with lamb"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/5l5zs76m_IMG-20250616-WA0009.jpg",
      alt: "Breakfast on the terrace"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/541jhgmv_IMG-20250706-WA0011.jpg",
      alt: "Fire ceremony"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mindful-container/artifacts/rj48rqts_0S0A3683.jpg",
      alt: "Inside space - healing room"
    }
  ];

  return (
    <main className="min-h-screen bg-[#FCF7EC] pt-20">
      {/* Hero - Text Left, Image Right */}
      <section className="py-12 md:py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Text - Left */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs tracking-[0.2em] uppercase font-sans mb-3" style={{ color: '#FFBF00' }}>
                {t('setting.label', language)}
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-spruce mb-4" dangerouslySetInnerHTML={{ __html: t('setting.title', language) }} />
              <p className="text-sm md:text-base text-deep-teal font-sans leading-relaxed">
                {t('setting.desc', language)}
              </p>
            </motion.div>

            {/* Main Image - Right */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/uys6wtw6_DJI_0133.jpg"
                alt="The Home - Aerial view"
                className="w-full rounded-lg ambient-shadow"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description - Green Banner */}
      <section className="py-12 px-6" style={{ backgroundColor: '#12552B' }}>
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            className="text-sm md:text-base font-sans leading-relaxed"
            style={{ color: '#FFBF00' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {t('setting.bannerLine1', language)}<br />
            {t('setting.bannerLine2', language)}
          </motion.p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            className="text-xs tracking-[0.2em] uppercase font-sans mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            style={{ color: '#FFBF00' }}
          >
            {t('setting.spaceLabel', language)}
          </motion.h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full rounded-lg h-48 md:h-64 object-cover"
                  style={image.position ? { objectPosition: image.position } : {}}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6" style={{ backgroundColor: '#F1E6D3' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-spruce mb-6">
            {t('setting.experienceTitle', language)}
          </h2>
          <p className="text-deep-teal font-sans mb-8">
            {t('setting.experienceDesc', language)}
          </p>
          <Link
            to="/contact"
            className="inline-block btn-primary px-10 py-4 font-sans text-base tracking-wide rounded-full"
          >
            {t('nav.bookCall', language)}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default TheHomePage;
